import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../utils/auth";
import {
  fetchAvailablePrintOrders,
  addOrdersToPrintQueue,
} from "../../api/PrintQueueAPIs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronRight,
  Search,
  Plus,
  CheckSquare,
  Square,
  Package,
  Loader2,
  Filter,
} from "lucide-react";

const STATUS_FILTERS = [
  { key: "New", label: "جديدة", activeClass: "bg-blue-600 text-white border-blue-600" },
  { key: "Confirmed", label: "مؤكدة", activeClass: "bg-emerald-600 text-white border-emerald-600" },
  { key: "Girabatcom", label: "Girabatcom", activeClass: "bg-cyan-600 text-white border-cyan-600" },
  { key: "Changes", label: "تحت التغييرات", activeClass: "bg-purple-600 text-white border-purple-600" },
  { key: "Deposit", label: "دفعة أولية", activeClass: "bg-orange-600 text-white border-orange-600" },
];

const DEFAULT_STATUSES = ["Girabatcom"];
const AddToPrintQueuePage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState(DEFAULT_STATUSES);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadOrders = useCallback(
    async (pageNum, searchTerm, statuses, append = false) => {
      try {
        const data = await fetchAvailablePrintOrders(pageNum, 20, searchTerm, statuses);
        if (data.length < 20) setHasMore(false);
        setOrders((prev) => (append ? [...prev, ...data] : data));
      } catch (err) {
        toast.error(err.message || "فشل تحميل الطلبات");
      }
    },
    []
  );

  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    setIsLoading(true);
    setPage(0);
    setHasMore(true);
    loadOrders(0, search, selectedStatuses).finally(() => setIsLoading(false));
  }, [authState, navigate, search, selectedStatuses, loadOrders]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setSelectedIds(new Set());
  };

  const toggleStatusFilter = (statusKey) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(statusKey)) {
        const next = prev.filter((s) => s !== statusKey);
        return next.length > 0 ? next : prev;
      }
      return [...prev, statusKey];
    });
    setSelectedIds(new Set());
  };

  const selectAllStatuses = () => {
    setSelectedStatuses(DEFAULT_STATUSES);
    setSelectedIds(new Set());
  };

  const loadMore = async () => {
    setIsLoadingMore(true);
    const nextPage = page + 1;
    await loadOrders(nextPage, search, selectedStatuses, true);
    setPage(nextPage);
    setIsLoadingMore(false);
  };

  const toggleOrder = (orderId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const selectableOrders = useMemo(
    () => orders.filter((o) => !o.isFullyQueued),
    [orders]
  );

  const selectableIds = useMemo(
    () => selectableOrders.map((o) => o.orderId),
    [selectableOrders]
  );

  const selectAllOrders = () => {
    if (selectableIds.length === 0) {
      toast.warning("لا توجد طلبات قابلة للتحديد");
      return;
    }
    setSelectedIds(new Set(selectableIds));
    toast.info(`تم تحديد ${selectableIds.length} طلب`);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const toggleAll = () => {
    if (selectableIds.length === 0) return;
    if (selectableIds.every((id) => selectedIds.has(id))) {
      clearSelection();
    } else {
      selectAllOrders();
    }
  };

  const handleAddToQueue = async () => {
    if (selectedIds.size === 0) {
      toast.warning("يرجى اختيار طلب واحد على الأقل");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await addOrdersToPrintQueue([...selectedIds]);
      const parts = [`تمت إضافة ${result.addedCount} جراب`];
      if (result.skippedDuplicates > 0) {
        parts.push(`${result.skippedDuplicates} مكرر`);
      }
      if (result.skippedInvalid > 0) {
        parts.push(`${result.skippedInvalid} غير صالح`);
      }
      toast.success(parts.join(" — "));
      setSelectedIds(new Set());
      navigate("/production");
    } catch (err) {
      toast.error(err.message || "فشل الإضافة للطابور");
    } finally {
      setIsSubmitting(false);
    }
  };

  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  return (
    <div
      dir="rtl"
      className="min-h-screen bg-gradient-to-br from-slate-100 via-indigo-50/30 to-slate-100 p-4 md:p-6"
    >
      <ToastContainer position="top-center" rtl />

      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/production")}
            className="p-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 transition"
          >
            <ChevronRight size={22} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">إضافة طلبات لطابور الطباعة</h1>
            <p className="text-slate-500 text-sm">اختر الفئة والطلبات — سيتم توسيع كل طلب إلى جرابات فردية</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <Filter size={18} />
              <span>فلترة حسب الفئة</span>
            </div>
            <button
              type="button"
              onClick={selectAllStatuses}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
            >
              اختيار كل الفئات
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((filter) => {
              const isActive = selectedStatuses.includes(filter.key);
              return (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => toggleStatusFilter(filter.key)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                    isActive
                      ? filter.activeClass
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {filter.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-slate-500">
            يمكنك اختيار فئة واحدة أو أكثر. يجب اختيار فئة واحدة على الأقل.
          </p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="بحث برقم الطلب أو اسم العميل أو الهاتف..."
              className="w-full pr-10 pl-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            />
          </div>
          <button
            type="submit"
            className="px-5 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition"
          >
            بحث
          </button>
        </form>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap bg-slate-50/80">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={toggleAll}
                disabled={selectableIds.length === 0}
                className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-indigo-600 disabled:opacity-50"
              >
                {allSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                {allSelected ? "إلغاء تحديد الكل" : "تحديد الكل"}
              </button>
              <button
                type="button"
                onClick={selectAllOrders}
                disabled={selectableIds.length === 0 || allSelected}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 disabled:opacity-50 transition"
              >
                تحديد كل الطلبات ({selectableIds.length})
              </button>
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-sm text-slate-500 hover:text-red-600"
                >
                  مسح التحديد
                </button>
              )}
            </div>
            <span className="text-sm text-slate-500 font-medium">
              {selectedIds.size} محدد من {selectableIds.length}
            </span>
          </div>

          {isLoading ? (
            <div className="p-8 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="p-12 text-center text-slate-400">
              <Package size={40} className="mx-auto mb-3 opacity-40" />
              لا توجد طلبات في الفئات المحددة
            </div>
          ) : (
            <div className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
              {orders.map((order) => {
                const isSelected = selectedIds.has(order.orderId);
                const disabled = order.isFullyQueued;
                return (
                  <button
                    key={order.orderId}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && toggleOrder(order.orderId)}
                    className={`w-full flex items-center gap-4 px-4 py-4 text-right transition ${
                      disabled
                        ? "opacity-50 cursor-not-allowed bg-slate-50"
                        : isSelected
                        ? "bg-indigo-50 hover:bg-indigo-100/80"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="text-indigo-600">
                      {disabled ? (
                        <CheckSquare size={22} className="text-emerald-500" />
                      ) : isSelected ? (
                        <CheckSquare size={22} />
                      ) : (
                        <Square size={22} className="text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-800">#{order.orderId}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {order.status}
                        </span>
                        {disabled && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                            في الطابور بالكامل
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 truncate">{order.customerName}</p>
                      <p className="text-xs text-slate-400">{order.phoneNumber}</p>
                    </div>
                    <div className="text-left shrink-0">
                      <p className="text-lg font-bold text-indigo-600">{order.totalCases}</p>
                      <p className="text-[10px] text-slate-400">جراب</p>
                      {order.alreadyQueuedCases > 0 && !disabled && (
                        <p className="text-[10px] text-amber-600">
                          {order.alreadyQueuedCases} في الطابور
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {hasMore && !isLoading && orders.length > 0 && (
            <div className="p-4 border-t border-slate-100 text-center">
              <button
                onClick={loadMore}
                disabled={isLoadingMore}
                className="px-6 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition disabled:opacity-50"
              >
                {isLoadingMore ? (
                  <span className="flex items-center gap-2 justify-center">
                    <Loader2 size={16} className="animate-spin" /> جاري التحميل...
                  </span>
                ) : (
                  "تحميل المزيد"
                )}
              </button>
            </div>
          )}
        </div>

        <div className="sticky bottom-4 flex justify-center">
          <button
            onClick={handleAddToQueue}
            disabled={isSubmitting || selectedIds.size === 0}
            className="flex items-center gap-2 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <Loader2 size={22} className="animate-spin" />
            ) : (
              <Plus size={22} />
            )}
            إضافة للطابور ({selectedIds.size} طلب)
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToPrintQueuePage;
